<script setup lang="ts">
import type {
  IMainNewsItemDataClientDTO,
  IMainNewsDataClientDTO,
} from '~/shared/api/client/main-news/main-news.dto';
import { useMainNewsList, useMainNewsSelected } from '~/shared/lib/useMainNews';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { getMaterialPublicId } from '~/shared/lib/getMaterialLink';
import { resizeImage } from '~/shared/lib/resizeImage';
import {
  CLIENT_MAIN_NEWS_URL,
  CLIENT_MAIN_NEWS_KEY,
} from '~/shared/api/client/publisher/publisher.const';

const newsList = useMainNewsList();
const newsListSelected = useMainNewsSelected();

const { data } = await useCustomFetch<IMainNewsDataClientDTO[]>(
  CLIENT_MAIN_NEWS_URL,
  {
    method: 'GET',
    key: CLIENT_MAIN_NEWS_KEY,
  },
);

if (data.value && data.value.length > 0) {
  newsList.value = data.value.map((item) => item.publisher_news[0]!);
  if (!newsListSelected.value) {
    newsListSelected.value = data.value[0]!.publisher_news[0]!;
  }
}

const isMobileMode = computed(() => {
  const isTouchDevice = import.meta.client
    ? 'ontouchstart' in window || navigator.maxTouchPoints > 0
    : false;
  const isSmallScreen = useNuxtApp().$viewport.isLessOrEquals('tabletBig');
  return isTouchDevice && isSmallScreen;
});

const getItemLink = (item: IMainNewsItemDataClientDTO) => {
  if (item.news?.redirect && item.news.redirect.length > 0)
    return item.news.redirect;
  if (item.expert) return `/expert/${getMaterialPublicId(item.expert)}`;
  return `/${item.news?.type}/${item.news?.id}`;
};

const handleHover = (item: IMainNewsItemDataClientDTO) => {
  if (isMobileMode.value) {
    return;
  }
  newsListSelected.value = item;
};

const handleClick = ($event: MouseEvent, item: IMainNewsItemDataClientDTO) => {
  if (isMobileMode.value && item.id !== newsListSelected.value!.id) {
    newsListSelected.value = item;
    return;
  }

  const { href, target } = $event.currentTarget as HTMLAnchorElement;
  const isBlank = target === '_blank';

  if (isBlank) {
    navigateTo(href, {
      open: { target },
      external: true,
    });
  } else {
    const path = new URL(href, window.location.origin).pathname;
    navigateTo(path);
  }
};
</script>

<template>
  <div v-if="newsList.length > 0" :class="$style.news">
    <a
      v-for="(item, key) in newsList"
      :key="`newsList_${key}`"
      :class="[
        $style.news__item,
        {
          [$style.news__item_selected!]: item.id === newsListSelected?.id,
        },
      ]"
      :href="getItemLink(item)"
      :target="item.news?.redirect ? '_blank' : undefined"
      @mouseover="handleHover(item)"
      @click.stop.prevent="handleClick($event, item)"
    >
      {{ item.title }}
      <img
        v-if="item.news?.category?.image_category"
        :src="
          resizeImage(item.news?.category?.image_category.file_url, {
            width: 160,
          })
        "
        :alt="item.category || item.news?.category?.name || 'category'"
        :class="[$style.news__categoryImage]"
      />
      <Icon
        v-else-if="item.type_item"
        :class="[
          $style.news__icon,
          {
            [$style.news__icon_plus!]: item.type_item === 'cre-plus',
            [$style.news__icon_awards!]: item.type_item === 'cre-awards',
            [$style.news__icon_telegram!]: item.type_item === 'cre-telegram',
          },
        ]"
        :name="`my-icon:i-${item.type_item}`"
        :size="item.type_item === 'cre-plus' ? 20 : 24"
      />
    </a>
  </div>
</template>

<style lang="scss" module>
.news {
  display: flex;
  flex-flow: column nowrap;
  gap: var(--space-1x);
  padding: var(--space-2-5x);
  border-bottom: 1px solid var(--color-gray-line);
  border-right: 1px solid var(--color-gray-line);
  height: 100%;

  &__item {
    @include text_body1;
    position: relative;
    display: flex;
    flex-wrap: wrap;
    column-gap: 8px;
    line-height: 22px;
    color: var(--color-black-1);
    text-decoration: none;

    &_selected {
      color: var(--color-red-1);
    }
  }

  &__icon {
    position: relative;
    width: auto;

    &_plus {
      top: 1px;
    }
    &_awards {
      top: -1px;
    }
  }
  &__categoryImage {
    height: 15px;
    width: auto;
    align-self: center;
  }
}

@media (hover: hover) {
  .news__item {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (max-width: 479px) {
  .news {
    padding: var(--space-2x);
    gap: var(--space-1-5x);

    &__item {
      line-height: 20px;
    }
  }
}
</style>
